import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { lazy, Suspense } from "react";

import { ArrowRight, Facebook, Check } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Reveal } from "@/components/Reveal";
import { FacebookFeed } from "@/components/FacebookFeed";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { HomeGalleryPreview } from "@/components/HomeGalleryPreview";
import { Button } from "@/components/ui/button";

const EnquiryForm = lazy(() => import("@/components/EnquiryForm").then(m => ({ default: m.EnquiryForm })));
const EventsSlider = lazy(() => import("@/components/EventsSlider").then(m => ({ default: m.EventsSlider })));
const BookingCalendar = lazy(() => import("@/components/BookingCalendar").then(m => ({ default: m.BookingCalendar })));
import { getUpcomingEvents, getGallery, getBookedDates, getAdvertisements, getFacebookPosts } from "@/lib/api/public-data.functions";
import { SITE } from "@/config/site";
import heroAvif480 from "@/assets/hero-cover.webp?w=480&format=avif&quality=55";
import heroAvif800 from "@/assets/hero-cover.webp?w=800&format=avif&quality=55";
import heroAvif1280 from "@/assets/hero-cover.webp?w=1280&format=avif&quality=55";
import heroAvif1920 from "@/assets/hero-cover.webp?w=1920&format=avif&quality=58";
import heroWebp480 from "@/assets/hero-cover.webp?w=480&format=webp&quality=65";
import heroWebp800 from "@/assets/hero-cover.webp?w=800&format=webp&quality=65";
import heroWebp1280 from "@/assets/hero-cover.webp?w=1280&format=webp&quality=68";
import heroWebp1920 from "@/assets/hero-cover.webp?w=1920&format=webp&quality=70";
const heroAvifSrcSet = `${heroAvif480} 480w, ${heroAvif800} 800w, ${heroAvif1280} 1280w, ${heroAvif1920} 1920w`;
const heroWebpSrcSet = `${heroWebp480} 480w, ${heroWebp800} 800w, ${heroWebp1280} 1280w, ${heroWebp1920} 1920w`;
import svcVersatile from "@/assets/svc-versatile.jpg?w=720&format=webp&quality=72";
import svcBollywood from "@/assets/svc-bollywood.jpg?w=720&format=webp&quality=72";
import svcBengali from "@/assets/svc-bengali.jpg?w=720&format=webp&quality=72";
import svcLive from "@/assets/svc-live.jpg?w=720&format=webp&quality=72";
import svcMusicalNight from "@/assets/svc-musical-night.jpg?w=720&format=webp&quality=72";
import svcVoiceover from "@/assets/svc-voiceover.jpg?w=720&format=webp&quality=72";
import svcDubbing from "@/assets/svc-dubbing.jpg?w=720&format=webp&quality=72";
import svcAlbums from "@/assets/svc-albums.jpg?w=720&format=webp&quality=72";
import svcPlayback from "@/assets/svc-playback.jpg?w=720&format=webp&quality=72";
import svcCorporate from "@/assets/svc-corporate.jpg?w=720&format=webp&quality=72";
import svcWedding from "@/assets/svc-wedding.jpg?w=720&format=webp&quality=72";
import svcCultural from "@/assets/svc-cultural.jpg";

const homeData = queryOptions({
  queryKey: ["home-data"],
  queryFn: async () => {
    const [events, gallery, booked, ads, fbPosts] = await Promise.all([
      getUpcomingEvents(),
      getGallery(),
      getBookedDates(),
      getAdvertisements(),
      getFacebookPosts(),
    ]);
    return { events, gallery, booked, ads, fbPosts };
  },
});

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { name: "robots", content: "index, follow" },
      { title: "Surer Adda With Sanchayeeta · Live Singer & Voice Artist" },
      { name: "description", content: "Book Sanchayeeta Seal for live performance, versatile singing, voice over & music albums across India — weddings, corporate, cultural & musical nights." },
      { name: "keywords", content: "Live Performance, Female Singer India, Versatile Singer, Bengali Singer, Stage Performer, Musical Night Performer, Corporate Event Performer, Wedding Singer, Voice Artist, Voice Over Artist, Voice Dubbing Artist, Music Album Singer, Cultural Program Singer, Professional Singer Booking, Event Entertainment India" },
      { property: "og:title", content: "Surer Adda With Sanchayeeta | Live Performance & Versatile Singer" },
      { property: "og:description", content: "Live Performance • Versatile Singing • Voice Artist • Voice Dubbing • Music Album — Creating Magical Musical Moments Across India." },
      { property: "og:url", content: `${SITE.url}/` },
    ],
    links: [
      { rel: "canonical", href: `${SITE.url}/` },
      { rel: "preload", as: "image", href: heroAvif800, imageSrcSet: heroAvifSrcSet, imageSizes: "100vw", fetchPriority: "high", type: "image/avif" } as any,
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Sanchayeeta Seal",
        jobTitle: "Live Performance Artist, Versatile Singer, Voice Artist",
        url: "/",
        sameAs: [SITE.facebookUrl],
        description: "Live performance artist, versatile singer, voice over & dubbing artist and music album performer based in India.",
      }),
    }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(homeData),
  component: Home,
  errorComponent: ({ error }) => { console.error(error); return <div className="p-10 text-center">Something went wrong loading this page.</div>; },
});

const services = [
  { title: "Versatile Singing", desc: "Versatile across classical, semi-classical & contemporary styles.", image: svcVersatile, alt: "Sanchayeeta Seal performing versatile Indian classical and semi-classical vocals on stage", seoTitle: "Versatile Singer — Classical, Semi-Classical & Contemporary Live Vocals" },
  { title: "Bollywood Singing", desc: "Hindi film hits — retro to contemporary chartbusters.", image: svcBollywood, alt: "Bollywood live concert silhouette with sparkling pink and gold stage lights", seoTitle: "Bollywood Singer for Live Shows — Retro to Contemporary Hindi Hits" },
  { title: "Bengali Modern Songs", desc: "Adhunik, Rabindra Sangeet & Bengali favourites.", image: svcBengali, alt: "Bengali musicians performing Adhunik and Rabindra Sangeet with tanpura and harmonium", seoTitle: "Bengali Modern Songs Singer — Adhunik, Rabindra Sangeet & Folk" },
  { title: "Live Stage Shows", desc: "High-energy live concerts across India.", image: svcLive, alt: "High-energy live concert crowd lit by blue and amber spotlights", seoTitle: "Live Stage Show Performer — High-Energy Concerts Across India" },
  { title: "Musical Nights", desc: "Curated musical evenings for every audience.", image: svcMusicalNight, alt: "Elegant candlelit musical night venue with grand piano on a small stage", seoTitle: "Musical Night Performer — Curated Evening Concert Experiences" },
  { title: "Voice Over", desc: "Professional voice over for ads, films & promos.", image: svcVoiceover, alt: "Professional studio condenser microphone with pop filter and headphones for voice over recording", seoTitle: "Professional Voice Over Artist — Ads, Films, Promos & Corporate" },
  { title: "Voice Dubbing", desc: "Expressive dubbing for films, OTT & animation.", image: svcDubbing, alt: "Voice dubbing artist reading script in front of film playback screen", seoTitle: "Voice Dubbing Artist — Films, OTT Series & Animation" },
  { title: "Music Albums", desc: "Original albums, playback & studio recordings.", image: svcAlbums, alt: "Stack of music albums and vinyl records with studio headphones on a wooden desk", seoTitle: "Music Album Singer — Original Albums & Studio Recordings" },
  { title: "Playback Singing", desc: "Film & OTT playback vocals across languages.", image: svcPlayback, alt: "Playback singer recording film vocals in a studio with live orchestra", seoTitle: "Film Playback Singer — Multi-Language Film & OTT Vocals" },
  { title: "Corporate Events", desc: "Brand launches, annual days & corporate galas.", image: svcCorporate, alt: "Corporate gala main stage with blue LED backdrop and formal audience", seoTitle: "Corporate Event Singer — Brand Launches, Annual Days & Galas" },
  { title: "Wedding Performances", desc: "Sangeet, reception & wedding ceremony shows.", image: svcWedding, alt: "Indian wedding sangeet stage decorated with marigolds and fairy lights", seoTitle: "Wedding Singer — Sangeet, Reception & Ceremony Performances" },
  { title: "Cultural Programs", desc: "Festivals, cultural nights & community events.", image: svcCultural, alt: "Indian cultural festival stage with classical dancers and live musicians in the evening", seoTitle: "Cultural Program Performer — Festivals & Community Cultural Nights" },
];

function Home() {
  const { data } = useSuspenseQuery(homeData);

  return (
    <div className="min-h-screen bg-background animate-fade-in motion-reduce:animate-none">
      <SiteHeader />

      <main>
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <picture>
            <source type="image/avif" srcSet={heroAvifSrcSet} sizes="100vw" />
            <source type="image/webp" srcSet={heroWebpSrcSet} sizes="100vw" />
            <img src={heroWebp1280} alt="Sanchayeeta Seal performing live on stage" width={1920} height={1280} fetchPriority="high" decoding="async" className="hero-ken-burns h-full w-full object-cover object-[72%_20%] sm:object-[center_15%]" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/60 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/40 via-transparent to-transparent" />
        </div>
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 sm:pt-28 md:pb-36 md:pt-40">
          <div className="hero-fade-up max-w-3xl motion-reduce:animate-none">
            <span className="inline-block rounded-full border border-gold/40 bg-background/60 px-4 py-1 text-xs font-medium uppercase tracking-[0.2em] text-foreground/80 backdrop-blur">
              Live Performance · Versatile · Voice Artist · Music Album
            </span>
            <h1 className="mt-5 text-5xl leading-[1.05] text-foreground sm:text-7xl md:text-8xl">
              <span className="font-script text-6xl text-primary sm:text-8xl md:text-9xl">Surer Adda</span>
              <span className="mt-2 block text-3xl tracking-tight text-foreground/90 sm:text-5xl md:text-6xl">
                with Sanchayeeta
              </span>
              <span className="sr-only"> — Live Singer &amp; Voice Artist</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base text-foreground/90 sm:text-lg">
              Live Performance • Versatile Singing • Voice Artist • Voice Dubbing • Music Album
            </p>
            <p className="mt-3 max-w-xl font-script text-2xl text-red-600 sm:text-3xl">
              “Creating Magical Musical Moments Across India”
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">Book Live Performance <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/events"><Button size="lg" variant="outline">Upcoming Events</Button></Link>
              <a href="#enquiry"><Button size="lg" variant="outline">Contact Now</Button></a>
            </div>
          </div>
        </div>
      </section>

      {/* ADVERTISEMENTS + FACEBOOK FEED */}
      <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6">
        <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.25em] text-primary">Sponsored</p>
        {data.ads.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.ads.map((ad, i) => {
              const inner = (
                <div className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                  <ResponsiveImage src={ad.image_url} alt={ad.caption ?? "Advertisement banner"} widths={[320, 480, 640, 800]} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw" fallbackWidth={480} quality={65} className="aspect-[16/9] w-full object-cover transition duration-500 group-hover:scale-105" />
                  {ad.caption && <div className="p-3 text-sm text-foreground/80">{ad.caption}</div>}
                </div>
              );
              return (
                <Reveal key={ad.id} delay={(i % 3) * 90}>
                  {ad.link_url
                    ? <a href={ad.link_url} target="_blank" rel="noopener noreferrer">{inner}</a>
                    : <div>{inner}</div>}
                </Reveal>
              );
            })}
          </div>
        )}
        <Reveal delay={120} className="mt-10">
          <div className="mb-4 flex items-center justify-center gap-3">
            <span className="h-px flex-1 max-w-[80px] bg-border" />
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Latest from Facebook</p>
            <span className="h-px flex-1 max-w-[80px] bg-border" />
          </div>
          <FacebookFeed posts={data.fbPosts} />
        </Reveal>
      </section>


      {/* SERVICES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <Reveal className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">What I Offer</p>
          <h2 className="mt-2 text-3xl sm:text-4xl">A voice for every stage</h2>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={(i % 4) * 80}>
              <div title={s.seoTitle} className="group h-full overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-xl">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={s.image} alt={s.alt} title={s.seoTitle} loading="lazy" width={800} height={600} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <h3 className="absolute bottom-3 left-4 right-4 text-lg font-semibold text-white drop-shadow">{s.title}</h3>
                </div>
                <p className="p-4 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="mb-10 flex items-end justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Upcoming</p>
              <h2 className="mt-2 text-3xl sm:text-4xl">Live Shows & Events</h2>
            </div>
            <Link to="/events" className="hidden text-sm font-medium text-primary hover:underline sm:inline">View all →</Link>
          </Reveal>
          <Reveal delay={120}>
            {data.events.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground">
                New shows coming soon. Follow our Facebook page for announcements.
              </p>
            ) : (
              <Suspense fallback={<div className="h-80 rounded-2xl bg-card/50" />}>
                <EventsSlider events={data.events} />
              </Suspense>
            )}
          </Reveal>
        </div>
      </section>

      {/* BOOKING CALENDAR */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <Reveal className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Availability</p>
          <h2 className="mt-2 text-3xl sm:text-4xl">Next 30 Days</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-3 w-3 rounded-full bg-green-500 align-middle" />
            Green dates indicate confirmed bookings.
          </p>
        </Reveal>
        <Reveal delay={120}>
          <Suspense fallback={<div className="h-96 rounded-2xl bg-card/50" />}>
            <BookingCalendar bookedDates={data.booked} />
          </Suspense>
        </Reveal>
      </section>

      {/* GALLERY PREVIEW */}
      {data.gallery.length > 0 && (
        <HomeGalleryPreview items={data.gallery as any} />
      )}

      {/* ENQUIRY */}
      <section id="enquiry" className="mx-auto max-w-5xl px-4 py-20 sm:px-6">
        <Reveal>
          <div className="rounded-3xl border border-border bg-card p-6 shadow-xl sm:p-10">
            <div className="mb-8 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Quick Enquiry</p>
              <h2 className="mt-2 text-3xl sm:text-4xl">Tell us about your event</h2>
              <p className="mt-2 text-sm text-muted-foreground">We typically respond within 24 hours.</p>
            </div>
            <Suspense fallback={<div className="h-96 rounded-xl bg-muted/30" />}>
              <EnquirySliderInline />
            </Suspense>
          </div>
        </Reveal>
      </section>

      {/* FB CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <Reveal>
          <a
            href={SITE.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-between gap-4 rounded-3xl bg-primary p-8 text-primary-foreground sm:flex-row"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Stay in tune</p>
              <h3 className="mt-2 text-2xl sm:text-3xl">Follow Surer Adda on Facebook</h3>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-background/15 px-6 py-3 font-medium backdrop-blur transition hover:bg-background/25">
              <Facebook className="h-5 w-5" /> Visit Page
            </span>
          </a>
        </Reveal>
      </section>
      </main>

      <SiteFooter />
    </div>
  );
}

// Mini slider: presents the form in 3 sequential steps
function EnquirySliderInline() {
  return <EnquiryForm compact />;
}
